import React, { useCallback, useEffect, useImperativeHandle, useState } from 'react';
import { Card, CardContent, Button, CardActions, Typography, Theme } from '@mui/material';
import ArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import ArrowDropDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { makeStyles, createStyles } from '@mui/styles';
import { useSelector } from 'react-redux';

import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { ICategoryTreeTable } from '../interfaces';
import { ICategoryItem } from '../../categories/interfaces';
import { IProductType } from '../../../../interfaces/productType';
import { IAppState } from '../../../../store/rootDuck';

const useTreeItemStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      color: theme.palette.text.primary,
      margin: 0,
      paddingLeft: 20,
      listStyle: 'none',
      '&:focus > $content, &$selected > $content': {
        backgroundColor: `var(--tree-view-bg-color)`,
        color: 'var(--tree-view-color)',
      },
      '&:focus > $content $label, &:hover > $content $label, &$selected > $content $label': {
        backgroundColor: 'transparent',
      },
      '&:hover > $content $label, &:hover > $content $label, &$selected > $content $label': {
        backgroundColor: 'transparent',
      },
    },
    cardCategory: {
      marginBottom: theme.spacing(3),
      [theme.breakpoints.down('sm')]: {
        display: 'none',
      },
    },
    cardButtons: {
      justifyContent: 'flex-end',
      padding: 16,
    },
    buttons: {
      marginRight: theme.spacing(2),
      // backgroundColor: ""
    },
    button: {
      '&:active': {
        opacity: 0.2,
      },
    },
    content: {
      color: theme.palette.text.primary,
      fontWeight: 500,
      '$expanded > &': {
        fontWeight: theme.typography.fontWeightRegular,
      },
    },
    group: {
      marginLeft: 0,
    },
    expanded: {},
    selected: {
      backgroundColor: `var(--tree-view-bg-color)`,
    },
    label: {
      fontWeight: 'inherit',
      color: 'inherit',
    },
    labelRoot: {
      display: 'flex',
      alignItems: 'center',
      paddingBottom: theme.spacing(0.7),
      paddingTop: theme.spacing(0.7),
      width: '100%',
      padding: 10,
      cursor: 'pointer',
      position: 'relative',
      transition: '.25s ease-out',
    },
    labelContainerChildren: {
      maxHeight: '100%',
      overflow: 'hidden',
    },
    labelChildren: {
      display: 'flex',
      alignItems: 'center',
      paddingBottom: theme.spacing(0.7),
      paddingTop: theme.spacing(0.7),
      width: '100%',
      padding: 10,
      cursor: 'pointer',
      paddingLeft: 30,
      transition: '.25s ease-out',
    },
    labelIcon: {},
    labelTitle: {
      fontWeight: 700,
      flexGrow: 1,
      fontSize: 14,
      color: '#282a3c',
    },
    labelText: {
      fontWeight: 600,
      flexGrow: 1,
      fontSize: 14,
      color: '#282a3c',
      // opacity: 0.8,
    },
    activeItem: {
      backgroundColor: theme.palette.secondary.main,

      borderRadius: 4,
    },
    hide: {
      height: 0,
    },
    itemText: {
      flexGrow: 1,
      color: '#282a3c',
      opacity: 0.8,
    },
    activeItemText: {
      color: theme.palette.primary.main,
    },
  })
);

type TestProps = {
  select: (id: number) => void;
  categories: ICategoryItem[];
  selectChildrenId: number | null;
  setSelectChildrenId: (id: number) => void;
  locale: string;
  setSelectedCategoryTypes?: (value: IProductType[]) => void;
  selectedCategoryId?: number | null;
  categoryIdActive?: number | null;
  isAdmin?: boolean;
};

export const StyledTreeItem = ({
  categories,
  select,
  setSelectChildrenId,
  selectChildrenId,
  locale,
  setSelectedCategoryTypes,
  selectedCategoryId,
  categoryIdActive,
  isAdmin,
}: TestProps) => {
  return (
    <li>
      {Object.entries(categories).map(([cartKey, item]) => {
        if (!isAdmin && !item.active) return null;
        return (
          <StyledTreeSubItem
            key={item.id?.toString() || cartKey?.toString()}
            category={item}
            select={select}
            selectChildrenId={selectChildrenId}
            setSelectChildrenId={setSelectChildrenId}
            locale={locale}
            setSelectedCategoryTypes={setSelectedCategoryTypes}
            selectedCategoryId={selectedCategoryId}
            categoryIdActive={categoryIdActive}
            isAdmin={isAdmin}
          />
        );
      })}
    </li>
  );
};

type TestPropsItem = {
  category: ICategoryItem;
  select: (id: number) => void;
  selectChildrenId: number | null;
  setSelectChildrenId: (id: number) => void;
  locale: string;
  setSelectedCategoryTypes?: (value: IProductType[]) => void;
  selectedCategoryId?: number | null;
  categoryIdActive?: number | null;
  isAdmin?: boolean;
};
export const StyledTreeSubItem = ({
  category,
  select,
  setSelectChildrenId,
  selectChildrenId,
  locale,
  setSelectedCategoryTypes,
  selectedCategoryId,
  categoryIdActive,
  isAdmin,
}: TestPropsItem) => {
  const classes = useTreeItemStyles();
  const [selectChild, setSelectChild] = useState<boolean>(false);
  const intl = useIntl();

  const handleItem = (id: number) => {
    select(id);
    category && category.id && setSelectChildrenId(category.id);
  };
  const onClickHandler = () => {
    setSelectChild(!selectChild);
  };

  useEffect(() => {
    if (selectedCategoryId?.toString() === category.id?.toString() && !selectChild) {
      onClickHandler();
      category &&
        setSelectedCategoryTypes &&
        setSelectedCategoryTypes(category.types?.map(i => i.type) || []);
      category && category.id && handleItem(category.id);
      category && category.id && setSelectChildrenId(category.id);
    }

    const findPathById = (arr: any, targetId: number | undefined | null, path = []): any[] => {
      if (!arr.length || !targetId) {
        return [];
      }

      for (const item of arr) {
        const newPath: any = [...path, { id: item.id }];

        if (item.id === targetId) {
          setSelectChild(true);
          return newPath;
        }

        if (item.children.length > 0) {
          const foundPath = findPathById(item.children, targetId, newPath);
          if (foundPath.length) {
            return foundPath;
          }
        }
      }

      return [];
    };
    findPathById(category.children, categoryIdActive);
  }, [selectedCategoryId, categoryIdActive]);

  return (
    <>
      <li style={{ marginLeft: '20px' }}>
        <div
          onClick={() => {
            onClickHandler();
            category &&
              setSelectedCategoryTypes &&
              setSelectedCategoryTypes(category.types?.map(i => i.type) || []);
            category && category.id && handleItem(category.id);
            category && category.id && setSelectChildrenId(category.id);
          }}
          className={clsx(classes.labelRoot, {
            [classes.activeItem]: category.id === selectChildrenId,
          })}
        >
          <div style={{ position: 'absolute', left: -20 }}>
            {Boolean(category.children?.length) &&
              (selectChild ? (
                <ArrowDropDownIcon style={{ color: '#6a96c3' }} />
              ) : (
                <ArrowRightIcon style={{ color: '#6a96c3' }} />
              ))}
          </div>
          <Typography
            variant='body2'
            style={{
              fontWeight: 'normal',
              fontSize: 16,
            }}
            className={clsx(classes.itemText, {
              [classes.activeItemText]: category.id === selectChildrenId,
            })}
          >
            {category.name}
          </Typography>
        </div>
        {/* поменять на !selectChild чтоб категории показывать  в развернутом виде */}
        {selectChild && category.children?.length ? (
          <StyledTreeItem
            categories={category.children}
            select={select}
            selectChildrenId={selectChildrenId}
            setSelectChildrenId={setSelectChildrenId}
            locale={locale}
            setSelectedCategoryTypes={setSelectedCategoryTypes}
            isAdmin={isAdmin}
            categoryIdActive={categoryIdActive}
          />
        ) : null}
      </li>
      {/* ) : null */}
      {/* } */}
    </>
  );
};

const FilterCategoriesTree: React.FC<ICategoryTreeTable> = ({
  categories,
  searchProducts,
  resetProducts,
  filter,
  setSearch,
  setSelectedCategoryId,
  selectedCategoryId,
  searchForCategory,
  setCategoryTypes,
  companyId,
  isAdmin,
  isVender,
  perPage = 10,
  categoriesRef,
  hideFilters,
}) => {
  const intl = useIntl();
  const classes = useTreeItemStyles();
  const [selectChildrenId, setSelectChildrenId] = useState<number>(0);
  const search = useSelector((state: IAppState) => state.productsCatalog.search);

  const { categoryIdActive } = useSelector((store: IAppState) => ({
    categoryIdActive: store.productsCatalog.categoryIdActive,
  }));

  useEffect(() => {
    if (categoryIdActive) {
      if (categoryIdActive !== selectChildrenId) {
        setSelectChildrenId(categoryIdActive);
        setSelectedCategoryId(categoryIdActive);
      }
    }
  }, [categoryIdActive]);

  useEffect(() => {
    selectedCategoryId && fetch();
  }, [selectedCategoryId]);

  const fetch = useCallback(() => {
    setSearch(true);
    searchForCategory && searchForCategory(selectChildrenId);
    if (searchProducts && selectChildrenId) {
      if (selectedCategoryId && companyId) {
        if (isAdmin || isVender) {
          searchProducts({
            // page: 1,
            perPage,
            filter,
            companyId,
            categoryId: selectChildrenId,
            search,
          });
        } else {
          searchProducts({
            // page: 1,
            perPage,
            filter,
            companyId,
            categoryId: selectChildrenId,
            active: true,
            search,
          });
        }
      } else if (selectedCategoryId && filter && !companyId) {
        if (isAdmin) {
          searchProducts({
            // page: 1,
            perPage,
            filter,
            companyId,
            categoryId: selectChildrenId,
            search,
          });
        } else {
          searchProducts({
            // page: 1,
            perPage,
            filter,
            companyId,
            categoryId: selectChildrenId,
            active: true,
            search,
          });
        }
      }
    }
  }, [
    selectedCategoryId,
    filter,
    searchProducts,
    selectChildrenId,
    searchForCategory,
    companyId,
    search,
  ]);

  const select = (id: number) => {
    setSelectedCategoryId(id);
  };

  const clear = () => {
    resetProducts();
    if (filter && filter.parameters.length === 0 && !filter.price_from && !filter.price_to) {
      setSearch(false);
    }
    setCategoryTypes && setCategoryTypes([]);
    setSelectedCategoryId(0);
    setSelectChildrenId(0);
  };

  useImperativeHandle(categoriesRef, () => ({
    resetCategories: () => {
      clear();
    },
  }));

  return (
    <>
      {!hideFilters && (
        <Card className={classes.cardCategory}>
          <CardContent>
            <Typography
              variant='h5'
              className={classes.labelTitle}
              style={{ marginBottom: 16, fontSize: 20 }}
            >
              {intl.formatMessage({ id: 'CATEGORIES.TITLE' })}
            </Typography>
            <ul className={classes.root}>
              <StyledTreeItem
                categories={categories}
                select={select}
                setSelectChildrenId={setSelectChildrenId}
                selectChildrenId={selectChildrenId}
                locale='ru'
                setSelectedCategoryTypes={setCategoryTypes}
                selectedCategoryId={selectedCategoryId}
                categoryIdActive={categoryIdActive}
                isAdmin={isAdmin}
              />
            </ul>
          </CardContent>
          <CardActions className={classes.cardButtons}>
            <Button onClick={clear} className={classes.buttons} variant='text' color='success'>
              {intl.formatMessage({ id: 'PRODUCT.FILTER.RESET' })}
            </Button>
          </CardActions>
        </Card>
      )}
    </>
  );
};

export default FilterCategoriesTree;
